import useRedirectUrl from "@casino/legacy/client/components/Auth/hooks/useRedirectUrl";
import emailPattern from "@casino/legacy/client/components/Auth/Shared/utils/emailPattern";
import toGenericErrors from "@casino/legacy/client/components/Auth/Shared/utils/toGenericErrors";
import useAuthCaptchaClearance from "@casino/legacy/client/components/Auth/Shared/utils/useAuthCaptchaClearance";
import useHandleCompleteRegistration from "@casino/legacy/client/features/CompleteRegistration/hooks/useHandleCompleteRegistration";
import useAuthMetamaskUser from "@casino/legacy/client/features/Metamask/hooks/useAuthMetamaskUser";
import useSocialAuthLocale from "@casino/legacy/client/features/SocialAuth/hooks/useSocialAuthLocale";
import useForm from "@casino/legacy/client/hooks/form/useForm";
import useTranslation from "@casino/legacy/client/hooks/i18n/useTranslation";
import useQueryParam from "@casino/legacy/client/hooks/router/useQueryParam";
import useRouter from "@casino/legacy/client/hooks/router/useRouter";
import useSite from "@casino/legacy/client/hooks/useSite";
import deserializeErrors from "@casino/legacy/shared/utils/errors/deserializeErrors";
import dateObjectFromDateString from "@casino/legacyUi/form/FormDateInput/DateInput/useDateInput/helpers/dateObjectFromDateString";
import formatDateToMonthDayYear from "@casino/legacyUi/form/FormDateInput/DateInput/useDateInput/helpers/formatDateTime";
import getTimeFromDayMonthYear from "@casino/legacyUi/form/FormDateInput/DateInput/useDateInput/helpers/getTimeFromDayMonthYear";
import useCountryCodeSelect from "@casino/legacyUi/form/FormSelect/useCountryCodeSelect";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { type RefObject, useRef, useState } from "react";
import type { RegisterOptions } from "react-hook-form";
import type Reaptcha from "reaptcha";
import type FormValues from "./FormValues";
import useHandleRegister from "./useHandleRegister";
import useHandlePayloadResponse from "./useHandleRegister/useHanldePayloadResponse";

type Props = {
  isPopover?: boolean;
  onSuccess?: () => void;
  defaultValues?: {
    offers: boolean;
    offersSms?: boolean;
    offersCall?: boolean;
    acceptsTerms?: boolean;
    email?: string;
    username?: string;
  };
};

const useFormRegister = ({ onSuccess, isPopover, defaultValues }: Props) => {
  const [showEmailSuggestion, setShowEmailSuggestion] = useState(false);
  const [loading, setLoading] = useState(false);
  const { push } = useRouter();
  const redirectUrl = useRedirectUrl();
  const [isMetamask] = useQueryParam("metamask");
  const { siteConfig } = useSite();
  const socialAuthType = getCookie("social_auth_type");
  const {
    handlePayloadResponse,
    hasCaptchaRequiredError,
    loading: payloadLoading,
  } = useHandlePayloadResponse({ isPopover, onSuccess });

  const { handleRegister } = useHandleRegister(hasCaptchaRequiredError);
  const { handleMetamaskAuth } = useAuthMetamaskUser();
  const { loading: loadingSocials, handleCompleteRegistration } =
    useHandleCompleteRegistration();
  const [getLocale] = useSocialAuthLocale();
  const { isCaptchaClearedRequired } = useAuthCaptchaClearance();

  const {
    formState,
    control,
    errors,
    required,
    optional,
    handleSubmit,
    setValue,
    getValues,
    setErrors,
    setError,
    clearErrors,
    watch,
  } = useForm<FormValues>({
    defaultValues: defaultValues
      ? { ...defaultValues }
      : {
          offers: true,
          offersSms: true,
          offersCall: true,
          acceptsTerms: false,
        },
    validationMode: "all",
  });

  const acceptsTerms = watch("acceptsTerms");
  const offers = watch("offers");
  const offersSms = watch("offersSms");
  const offersCall = watch("offersCall");

  const offersLabel = useTranslation("profile.offers.site");
  const termsLabel = useTranslation(
    "profile.i_agree_to_terms_and_conditions_and_privacy_policy",
  );
  const { isValid, isDirty } = formState;

  const isSubmitDisabled = !isValid || !isDirty;

  const recaptchaRef = useRef<Reaptcha>(null);

  const callingCodes = useCountryCodeSelect<FormValues>({
    autofillFromGeo: false,
    onChange: setValue,
    value: getValues().countryCode || "",
  });

  const otherCountryCodesText = useTranslation("general.other_country_codes");

  const handleSelectedSuggestionEmail = (selectedEmail: string) => {
    const currentValue = getValues().email;
    const newValue = currentValue.concat(selectedEmail);
    setValue("email", newValue);
    setShowEmailSuggestion(false);
  };

  const currentDate = new Date();
  const defaultDate = formatDateToMonthDayYear(currentDate);

  const invalidUsernameErrorMsg = useTranslation("errors.INVALID_USERNAME");
  const invalidEmailErrorMsg = useTranslation("errors.INVALID_EMAIL");
  const longEmailErrorMsg = useTranslation("errors.input.max_length");

  const usernameInputOptions: RegisterOptions = {
    pattern: {
      value: /^[A-Za-z0-9_]{3,20}$/,
      message: invalidUsernameErrorMsg,
    },
  };

  const emailInputOptions: RegisterOptions = {
    maxLength: {
      value: 80,
      message: longEmailErrorMsg,
    },
    pattern: {
      value: emailPattern,
      message: invalidEmailErrorMsg,
    },
  };

  return {
    control,
    offersLabel,
    formState,
    termsLabel,
    loading: loading || loadingSocials || payloadLoading,
    errors,
    isCaptchaRequired: isCaptchaClearedRequired || hasCaptchaRequiredError,
    recaptchaRef: recaptchaRef as RefObject<Reaptcha>,
    callingCodes,
    isSubmitDisabled,
    required,
    setValue,
    getValues,
    optional,
    acceptsTerms,
    offers,
    offersSms,
    offersCall,
    setError,
    clearErrors,
    otherCountryCodesText,
    showEmailSuggestion,
    handleSelectedSuggestionEmail,
    handlePayloadResponse,
    usernameInputOptions,
    emailInputOptions,
    handleSubmit: handleSubmit(async (values) => {
      setLoading(true);
      clearErrors();
      const { DateTime } = await import("luxon");
      const dateOfBirth =
        typeof values.dateOfBirth === "string"
          ? dateObjectFromDateString(values.dateOfBirth ?? defaultDate)
          : values.dateOfBirth;
      const isDateOfBirthValid = DateTime.fromObject(
        values.dateOfBirth,
      ).isValid;

      const timestamp = getTimeFromDayMonthYear(dateOfBirth);

      if (!isDateOfBirthValid || timestamp === undefined) {
        setLoading(false);
        return setError("dateOfBirth.day", { message: "errors.INVALID_DATE" });
      }

      if (isMetamask) {
        return handleMetamaskAuth({
          values: { ...values, dateOfBirth: timestamp },
          setErrors,
          setError,
          redirectUrl,
        });
      }

      const socialAuthError = sessionStorage.getItem("social_auth_error");
      if (socialAuthType && !socialAuthError) {
        const payload = await handleCompleteRegistration({
          ...values,
          dateOfBirth: timestamp,
        });
        if (payload.errors) {
          recaptchaRef.current?.reset();
          const payloadErrors = toGenericErrors(payload.errors);
          setErrors(payloadErrors);
          const deserializedErrors = deserializeErrors(
            payloadErrors,
          ) as DeserializeError;
          setLoading(false);
          if (deserializedErrors.dateOfBirth) {
            setLoading(false);
            const [message] = deserializedErrors.dateOfBirth;
            setError("dateOfBirth.day", { message: `errors.${message}` });
          }
          return;
        }
        return push(redirectUrl, undefined, {
          shallow: false,
          locale:
            getLocale() === siteConfig.defaultLang ? undefined : getLocale(),
        });
      }
      const payload = await handleRegister({
        ...values,
        dateOfBirth: timestamp,
      });
      setLoading(false);
      if (payload.errors) {
        recaptchaRef.current?.reset();
        const payloadErrors = toGenericErrors(payload.errors);
        setErrors(payloadErrors);
        const deserializedErrors = deserializeErrors(
          payloadErrors,
        ) as DeserializeError;
        if (
          deserializedErrors.email &&
          deserializedErrors.email[0] === "EMAIL_ALREADY_EXISTS"
        ) {
          setCookie("existingEmail", values.email);
          setLoading(false);
          push("/login");
        }
      }
      return handlePayloadResponse(payload, setErrors, () => {
        window.gib?.setAttribute("email", values.email, {
          persistent: false,
          encryption: null,
        });
      });
    }),
  };
};

export default useFormRegister;
